import { ROOM_STATE } from 'asteroids-shared/protocol'
import GameAudio from './audio/GameAudio.js'
import AppRoomSocket from './AppRoomSocket.js'
import * as AppMenuActions from './AppMenuActions.js'
import { CSS, attachStylesheet, setupCssHoverPreload } from './styles/StylesPreloader.js'
import Game from './components/Game.js'
import { renderLobby, shakeLobbyBell } from './components/Lobby.js'
import { renderMenu } from './components/Menu.js'

export default class App {
  #gameView = null
  #pollRoomsId = null
  #teardownCssPreload = null

  constructor(root) {
    this.root = root
    this.audio = new GameAudio()
    this.disconnectMessage = null
    this.roomSocket = new AppRoomSocket()

    this.connected = false
    this.myId = null
    this.lobby = null
    this.rooms = []
    this.view = 'menu'
    this.error = null
    this.errorPlacement = null
    this.gameOver = null
    this.highScores = []
    this.roundStart = null
    this.menu = AppMenuActions.createInitialMenuState()

    this.roomSocket.setHandlers({
      onConnected: () => {
        this.connected = true
      },
      onWelcome: message => {
        this.disconnectMessage = null
        this.myId = message.playerId
        this.error = null
        this.errorPlacement = null
      },
      onLobbyState: message => {
        this.lobby = message

        if (message.state === ROOM_STATE.WAITING) {
          this.view = 'lobby'
        } else if (message.state === ROOM_STATE.PLAYING) {
          this.view = 'game'
        } else if (message.state === ROOM_STATE.ENDED) {
          const iReturned = message.postMatchReturnedIds?.includes(this.myId)
          if (this.gameOver && !iReturned) {
            this.view = 'game'
          } else {
            this.view = 'lobby'
          }
        }

        this.render()
      },
      onGameStart: message => {
        this.roundStart = message
        if (this.lobby && message.mode != null && message.mode !== '') {
          this.lobby = {
            ...this.lobby,
            mode: message.mode,
            toroidalMap: message.toroidalMap ?? this.lobby.toroidalMap,
            state: ROOM_STATE.PLAYING,
            players: message.players ?? this.lobby.players,
            matchEndsAtServerTime: message.matchEndsAtServerTime ?? null
          }
        }
        this.gameOver = null
        this.audio.playStartChime()
        this.view = 'game'
        this.render()
      },
      onLobbyBell: message => {
        void this.audio.resume()
        if (message.kind === 'player_joined' && message.playerId !== this.myId) {
          this.audio.playLobbyPlayerJoinedBell()
        } else if (message.kind === 'ring') {
          this.audio.playLobbyRingBell()
          if (message.playerId !== this.myId) {
            shakeLobbyBell(this.root)
          }
        }
      },
      onGameOver: message => {
        this.gameOver = {
          mode: message.mode,
          reason: message.reason,
          finalScore: message.finalScore,
          wavesCleared: message.wavesCleared,
          standings: message.standings ?? null,
          winnerId: message.winnerId ?? null,
          winnerTeamId: message.winnerTeamId ?? null,
          teamScores: message.teamScores ?? null,
          returnLobbyCapMs: message.returnLobbyCapMs ?? null
        }
        this.view = 'game'
        this.render()
      },
      onKicked: () => {
        this.disconnectMessage = 'You were kicked from the room.'
      },
      onError: message => {
        this.error = AppMenuActions.formatServerError(message.message)
        this.errorPlacement = 'global'
        this.render()
      },
      onDisconnect: ({ ignored }) => {
        this.lobby = null
        this.myId = null
        this.gameOver = null
        this.roundStart = null
        this.view = 'menu'

        if (!ignored) {
          this.error = this.disconnectMessage || 'Disconnected from server'
          this.errorPlacement = 'global'
        }

        this.disconnectMessage = null
        this.render()
        void AppMenuActions.refreshData(this)
      }
    })
  }

  async start() {
    await AppMenuActions.refreshData(this)
    this.render()
    AppMenuActions.consumeReplayQueryOnLoad(this)
    this.#teardownCssPreload = setupCssHoverPreload(this.root)

    this.#pollRoomsId = setInterval(() => {
      if (this.view === 'menu') void AppMenuActions.refreshData(this)
    }, 5000)
  }

  destroy() {
    if (this.#pollRoomsId) clearInterval(this.#pollRoomsId)
    this.#pollRoomsId = null
    this.#teardownCssPreload?.()
    this.#teardownCssPreload = null
    this.destroyGameView()
    this.roomSocket.close(true)
    this.audio.destroy()
  }

  currentState() {
    return {
      connected: this.connected,
      myId: this.myId,
      lobby: this.lobby,
      rooms: this.rooms,
      view: this.view,
      error: this.error,
      errorPlacement: this.errorPlacement,
      gameOver: this.gameOver,
      roundStart: this.roundStart,
      highScores: this.highScores,
      menu: this.menu
    }
  }

  render() {
    this.root.dataset.view = this.view

    if (this.view === 'lobby') attachStylesheet(CSS.lobby)
    if (this.view === 'game') attachStylesheet(CSS.round)

    if (this.view !== 'game') {
      this.destroyGameView()
    }

    if (this.view === 'game' && this.lobby && this.myId) {
      if (this.#gameView && this.#gameView.roomId === this.lobby.roomId) {
        this.#gameView.updateRoster(this.lobby)
        this.#gameView.updateGameOver(this.gameOver)
        return
      }
      this.destroyGameView()
      const { players, ...roomWire } = this.lobby
      this.#gameView = new Game(this.root, {
        socket: this.roomSocket.getSocket(),
        localPlayerId: this.myId,
        room: {
          ...roomWire,
          mode: this.roundStart?.mode ?? roomWire.mode ?? 'coop',
          roster: players ?? [],
          name: this.lobby.roomName ?? '',
          returnLobby: () => this.returnLobby(),
          round: {
            start: {
              serverTime: this.roundStart?.serverTime ?? null,
              roundDuration: this.roundStart?.roundDuration ?? null
            },
            end: this.roundStart?.matchEndsAtServerTime ?? null
          }
        },
        spawn: {
          playerOrder: this.roundStart?.playerOrder ?? []
        },
        audio: this.audio,
        gameOver: this.gameOver
      })
      this.#gameView.mount()
      return
    }

    if (this.view === 'lobby' && this.lobby && this.myId) {
      renderLobby(this.root, this.currentState(), {
        toggleReady: ready => this.toggleReady(ready),
        castVote: vote => this.castVote(vote),
        kickVote: (targetId, vote) => this.kickVote(targetId, vote),
        leaveRoom: () => AppMenuActions.leaveRoom(this),
        openReplay: replayId => void AppMenuActions.openReplay(this, replayId),
        ringLobbyBell: () => {
          void this.audio.resume()
          this.roomSocket.ringLobbyBell()
        }
      })
      return
    }

    renderMenu(this.root, this.currentState(), {
      getState: () => this.currentState(),
      setName: value => AppMenuActions.setName(this, value),
      setRoomName: value => AppMenuActions.setRoomName(this, value),
      setCreatePrivate: value => AppMenuActions.setCreatePrivate(this, value),
      setScoreModeFilter: value => AppMenuActions.setScoreModeFilter(this, value),
      setJoinCode: value => AppMenuActions.setJoinCode(this, value),
      clearMenuError: () => AppMenuActions.clearMenuError(this),
      togglePrivateRoom: roomId => AppMenuActions.togglePrivateRoom(this, roomId),
      createGame: () => void AppMenuActions.createGame(this),
      joinRoom: (roomId, roomCode) => void AppMenuActions.joinRoom(this, roomId, roomCode),
      openReplay: replayId => void AppMenuActions.openReplay(this, replayId)
    })
  }

  destroyGameView() {
    this.#gameView?.destroy()
    this.#gameView = null
  }

  async connectToRoom({ name, wsPath, roomCode = '' }) {
    await this.roomSocket.connectToRoom({ name, wsPath, roomCode })
  }

  toggleReady(ready) {
    if (ready) this.audio.resume()
    this.roomSocket.setReady(ready)
  }

  kickVote(targetId, vote) {
    this.roomSocket.kickVote(targetId, vote)
  }

  castVote(vote) {
    this.roomSocket.castVote(vote)
  }

  returnLobby() {
    this.roomSocket.returnLobby()
  }
}
